import { useEffect, useMemo, useState, useCallback, useRef, memo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Button, Card, CardSection, Badge } from '@trafiflow/shared-ui'
import { Search, Trash2, Edit, Tag, MoreVertical, Eye, Plus, X } from 'lucide-react'
import { offerApi, type Offer } from '../lib/funnel-api'
import { getErrorMessage, type ApiError } from '../lib/api-error'
import { Pagination } from '../components/shared/Pagination'
import { ConfirmModal } from '../components/shared/ConfirmModal'
import { DialogPortal } from '../components/shared/DialogPortal'
import { inputClass, labelClass } from '../styles/class-utils'

interface OfferFormData {
  name: string
  description: string
  discountType: string
  discountValue: number
  isActive: boolean
}

const EMPTY_FORM: OfferFormData = {
  name: '',
  description: '',
  discountType: 'percentage',
  discountValue: 0,
  isActive: true,
}

function OfferModal({
  isOpen,
  editingOffer,
  onClose,
  onSaved,
}: {
  isOpen: boolean
  editingOffer: Offer | null
  onClose: () => void
  onSaved: () => void
}) {
  const [form, setForm] = useState<OfferFormData>(EMPTY_FORM)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  useEffect(() => {
    if (!isOpen) return
    if (editingOffer) {
      setForm({
        name: editingOffer.name,
        description: editingOffer.description || '',
        discountType: editingOffer.discountType || editingOffer.discountConfig?.valueType || 'percentage',
        discountValue: editingOffer.discountValue ?? (editingOffer.discountConfig?.value ? editingOffer.discountConfig.value / 100 : 0),
        isActive: editingOffer.isActive,
      })
    } else {
      setForm(EMPTY_FORM)
    }
    setSaveError(null)
  }, [isOpen, editingOffer])

  const handleSubmit = async () => {
    setSaving(true)
    setSaveError(null)
    try {
      if (editingOffer) {
        await offerApi.update(editingOffer.id, form)
      } else {
        await offerApi.create(form)
      }
      onSaved()
      onClose()
    } catch (err) {
      setSaveError(getErrorMessage(err as ApiError, 'Failed to save offer.'))
    } finally {
      setSaving(false)
    }
  }

  if (!isOpen) return null

  return (
    <DialogPortal>
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50 animate-fade-in" onClick={onClose} />
      <div className="relative z-10 w-full max-w-lg bg-fn-card rounded-lg shadow-fn-modal border border-fn-border animate-scale-in">
        <div className="flex items-center justify-between px-6 py-4 border-b border-fn-border">
          <h2 className="text-lg font-semibold text-fn-text">
            {editingOffer ? 'Edit Offer' : 'Create New Offer'}
          </h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-fn-hover transition-colors">
            <X className="w-5 h-5 text-fn-text-muted" />
          </button>
        </div>
        <div className="px-6 py-5 space-y-4">
          {saveError && (
            <div className="px-3 py-2 text-sm text-red-600 bg-tf-feedback-error-bg border border-tf-feedback-error-border rounded-md">
              {saveError}
            </div>
          )}
          <div>
            <label className={labelClass}>
              Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              className={inputClass}
              placeholder="Offer name"
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
            />
          </div>
          <div>
            <label className={labelClass}>Description</label>
            <textarea
              className={`${inputClass} resize-none`}
              rows={3}
              placeholder="Optional description"
              value={form.description}
              onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Discount Type</label>
              <select
                className={inputClass}
                value={form.discountType}
                onChange={(e) => setForm((p) => ({ ...p, discountType: e.target.value }))}
              >
                <option value="percentage">Percentage</option>
                <option value="fixed">Fixed Amount</option>
                <option value="bogo">BOGO</option>
              </select>
            </div>
            <div>
              <label className={labelClass}>Discount Value</label>
              <input
                type="number"
                className={inputClass}
                placeholder="0"
                value={form.discountValue}
                onChange={(e) => setForm((p) => ({ ...p, discountValue: parseFloat(e.target.value) || 0 }))}
              />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="offer-active"
              className="w-3.5 h-3.5 rounded border-tf-border text-primary focus:ring-primary"
              checked={form.isActive}
              onChange={(e) => setForm((p) => ({ ...p, isActive: e.target.checked }))}
            />
            <label htmlFor="offer-active" className="text-sm text-fn-text">Active</label>
          </div>
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-fn-border">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={handleSubmit} disabled={!form.name.trim() || saving}>
            {saving ? 'Saving...' : editingOffer ? 'Update' : 'Create'}
          </Button>
        </div>
      </div>
    </div>
    </DialogPortal>
  )
}

function getDiscountType(offer: Offer) {
  return offer.discountType || offer.discountConfig?.valueType || 'none'
}

function getDiscountValue(offer: Offer) {
  if (offer.discountValue != null) return offer.discountValue
  if (offer.discountConfig?.value != null) return offer.discountConfig.value / 100
  return 0
}

function formatDiscountValue(type: string, value: number) {
  switch (type) {
    case 'percentage': return `${value}%`
    case 'fixed': return `$${value}`
    case 'bogo': return 'BOGO'
    default: return String(value)
  }
}

function getDiscountBadgeVariant(type: string) {
  switch (type) {
    case 'percentage': return 'success' as const
    case 'fixed': return 'warning' as const
    default: return 'default' as const
  }
}

function OfferRowMenu({ offerId, onEdit, onDelete }: { offerId: string; onEdit: (id: string) => void; onDelete: (id: string) => void }) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open])

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(!open) }}
        className="p-1.5 rounded-md hover:bg-fn-hover text-fn-text-muted hover:text-fn-text-sub transition-colors"
        aria-label="Row actions"
      >
        <MoreVertical className="w-4 h-4" />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-40 bg-fn-card rounded-lg shadow-fn-dropdown border border-fn-border py-1 z-50 animate-scale-in">
          <button
            onClick={(e) => { e.stopPropagation(); onEdit(offerId); setOpen(false) }}
            className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-fn-text-sub hover:bg-fn-hover transition-colors"
          >
            <Eye className="w-4 h-4" />
            View
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onEdit(offerId); setOpen(false) }}
            className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-fn-text-sub hover:bg-fn-hover transition-colors"
          >
            <Edit className="w-4 h-4" />
            Edit
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(offerId); setOpen(false) }}
            className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-red-600 hover:bg-tf-danger-light transition-colors border-t border-fn-border mt-1 pt-2"
          >
            <Trash2 className="w-4 h-4" />
            Delete
          </button>
        </div>
      )}
    </div>
  )
}

const OfferRow = memo(function OfferRow({
  offer,
  onEdit,
  onDelete,
  onContextMenu,
}: {
  offer: Offer
  onEdit: (id: string) => void
  onDelete: (id: string) => void
  onContextMenu: (e: React.MouseEvent, offerId: string) => void
}) {
  const discountType = getDiscountType(offer)
  return (
    <tr className="border-t border-fn-border hover:bg-fn-hover transition-colors animate-fade-slide-in cursor-pointer" onDoubleClick={() => onEdit(offer.id)} onContextMenu={(e) => onContextMenu(e, offer.id)}>
      <td className="px-4 py-3">
        <div className="flex flex-col">
          <span className="font-medium text-fn-text">{offer.name}</span>
          {offer.description && (
            <span className="text-xs text-fn-text-muted line-clamp-1 mt-0.5">{offer.description}</span>
          )}
        </div>
      </td>
      <td className="px-4 py-3">
        <Badge variant={getDiscountBadgeVariant(discountType)}>
          {discountType}
        </Badge>
      </td>
      <td className="px-4 py-3">
        <span className="text-fn-text-muted font-medium">
          {formatDiscountValue(discountType, getDiscountValue(offer))}
        </span>
      </td>
      <td className="px-4 py-3">
        <Badge variant={offer.isActive ? 'success' : 'default'}>
          {offer.isActive ? 'Active' : 'Inactive'}
        </Badge>
      </td>
      <td className="px-4 py-3">
        <span className="text-fn-text-sub">
          {new Date(offer.createdAt).toLocaleDateString()}
        </span>
      </td>
      <td className="px-4 py-3">
        <OfferRowMenu offerId={offer.id} onEdit={onEdit} onDelete={onDelete} />
      </td>
    </tr>
  )
})

export default function OffersList() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [offers, setOffers] = useState<Offer[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null)
  const [isOfferModalOpen, setIsOfferModalOpen] = useState(false)
  const [editingOffer, setEditingOffer] = useState<Offer | null>(null)
  const ITEMS_PER_PAGE = 10

  const fetchOffers = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await offerApi.list()
      setOffers(Array.isArray(response.data) ? response.data : [])
    } catch (err) {
      console.error('Error fetching offers:', (err as ApiError).message)
      setError(getErrorMessage(err as ApiError, 'Failed to load offers.'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchOffers()
  }, [fetchOffers])

  const handleDeleteClick = useCallback((id: string) => {
    const offer = offers.find((o) => o.id === id)
    setDeleteTarget({ id, name: offer?.name || 'this offer' })
  }, [offers])

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget) return
    try {
      await offerApi.delete(deleteTarget.id)
      setDeleteTarget(null)
      await fetchOffers()
    } catch (err) {
      console.error('Error deleting offer:', (err as ApiError).message)
      window.alert(getErrorMessage(err as ApiError, 'Failed to delete offer.'))
    }
  }, [deleteTarget, fetchOffers])

  const handleEdit = useCallback((id: string) => {
    const offer = offers.find((o) => o.id === id)
    if (offer) {
      setEditingOffer(offer)
      setIsOfferModalOpen(true)
    }
  }, [offers])

  // Open edit modal when ?selected= query param is present
  useEffect(() => {
    const selectedId = searchParams.get('selected')
    if (selectedId && offers.length > 0) {
      const offer = offers.find((o) => o.id === selectedId)
      if (offer) {
        setEditingOffer(offer)
        setIsOfferModalOpen(true)
      }
      setSearchParams({}, { replace: true })
    }
  }, [searchParams, offers, setSearchParams])

  const handleOfferSaved = useCallback(() => {
    setEditingOffer(null)
    fetchOffers()
  }, [fetchOffers])

  const handleOpenCreate = useCallback(() => {
    setEditingOffer(null)
    setIsOfferModalOpen(true)
  }, [])

  // Context menu on right-click
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; offerId: string } | null>(null)

  const handleRowContextMenu = useCallback((e: React.MouseEvent, offerId: string) => {
    e.preventDefault()
    setContextMenu({ x: e.clientX, y: e.clientY, offerId })
  }, [])

  useEffect(() => {
    if (!contextMenu) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setContextMenu(null)
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [contextMenu])

  const filteredOffers = useMemo(() => {
    setCurrentPage(1)
    const query = searchQuery.toLowerCase()
    return offers.filter((offer) => {
      const matchesSearch =
        !searchQuery ||
        offer.name.toLowerCase().includes(query) ||
        offer.description?.toLowerCase().includes(query) ||
        offer.id.toLowerCase().includes(query)

      const matchesStatus =
        !statusFilter ||
        (statusFilter === 'active' && offer.isActive) ||
        (statusFilter === 'inactive' && !offer.isActive)

      return matchesSearch && matchesStatus
    })
  }, [offers, searchQuery, statusFilter])

  const paginatedOffers = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE
    return filteredOffers.slice(start, start + ITEMS_PER_PAGE)
  }, [filteredOffers, currentPage])

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-fn-text-muted">Loading offers...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-8 text-center bg-critical-lighter rounded-lg border border-critical">
        <p className="text-critical font-medium">{error}</p>
        <Button onClick={() => fetchOffers()} variant="primary" className="mt-4">
          Retry
        </Button>
      </div>
    )
  }

  return (
    <div className="funnels-theme space-y-6 animate-fade-in">
      <div className="flex flex-col gap-4 md:flex-row md:justify-between md:items-center">
        <div>
          <h1 className="text-2xl font-bold text-tf-text">Offers</h1>
          <p className="text-tf-text-muted">Manage your discount offers for funnels</p>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-fn-text-muted">
            Total: {filteredOffers.length} offer{filteredOffers.length !== 1 ? 's' : ''}
          </span>
          <Button variant="primary" onClick={handleOpenCreate}>
            <Plus className="w-4 h-4 mr-1" /> Create Offer
          </Button>
        </div>
      </div>

      <Card>
        <CardSection className="p-4">
          <div className="grid gap-3 md:grid-cols-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-fn-text-muted pointer-events-none" />
              <input
                type="text"
                placeholder="Search by name or description..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`${inputClass} pl-10`}
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className={inputClass}
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </CardSection>
      </Card>

      <Card>
        <CardSection className="p-0">
          <div className="overflow-x-auto rounded-lg border border-fn-border">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-fn-muted">
                  <th className="text-left px-4 py-3 font-medium text-fn-text-sub uppercase text-xs tracking-wide">Name</th>
                  <th className="text-left px-4 py-3 font-medium text-fn-text-sub uppercase text-xs tracking-wide">Discount</th>
                  <th className="text-left px-4 py-3 font-medium text-fn-text-sub uppercase text-xs tracking-wide">Value</th>
                  <th className="text-left px-4 py-3 font-medium text-fn-text-sub uppercase text-xs tracking-wide">Status</th>
                  <th className="text-left px-4 py-3 font-medium text-fn-text-sub uppercase text-xs tracking-wide">Created</th>
                  <th className="text-left px-4 py-3 font-medium text-fn-text-sub uppercase text-xs tracking-wide">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredOffers.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-12 text-center">
                      <Tag className="w-12 h-12 text-fn-text-muted mx-auto mb-3" />
                      <p className="text-fn-text-muted font-medium">No offers found</p>
                      <p className="text-fn-text-muted text-xs mt-1">Create your first offer to get started</p>
                      <Button variant="primary" className="mt-3" onClick={handleOpenCreate}>
                        <Plus className="w-4 h-4 mr-1" /> Create Offer
                      </Button>
                    </td>
                  </tr>
                ) : (
                  paginatedOffers.map((offer) => (
                    <OfferRow
                      key={offer.id}
                      offer={offer}
                      onEdit={handleEdit}
                      onDelete={handleDeleteClick}
                      onContextMenu={handleRowContextMenu}
                    />
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="px-4 pb-3">
            <Pagination
              currentPage={currentPage}
              totalItems={filteredOffers.length}
              itemsPerPage={ITEMS_PER_PAGE}
              onPageChange={setCurrentPage}
            />
          </div>
        </CardSection>
      </Card>

      {contextMenu && (
        <>
        <div className="fixed inset-0 z-40" onClick={() => setContextMenu(null)} onContextMenu={(e) => { e.preventDefault(); setContextMenu(null) }} />
        <div
          className="fixed w-40 bg-fn-card rounded-lg shadow-fn-dropdown border border-fn-border py-1 z-50 animate-scale-in"
          style={{ top: contextMenu.y, left: contextMenu.x }}
        >
          {[
            { label: 'View', icon: <Eye className="w-4 h-4" />, onClick: () => handleEdit(contextMenu.offerId) },
            { label: 'Edit', icon: <Edit className="w-4 h-4" />, onClick: () => handleEdit(contextMenu.offerId) },
            { label: 'Delete', icon: <Trash2 className="w-4 h-4" />, onClick: () => handleDeleteClick(contextMenu.offerId), danger: true },
          ].map((item, i, arr) => (
            <button
              key={item.label}
              onClick={() => { item.onClick(); setContextMenu(null) }}
              className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm transition-colors ${
                item.danger ? 'text-red-600 hover:bg-tf-danger-light' : 'text-fn-text-sub hover:bg-fn-hover'
              } ${i === arr.length - 1 ? 'border-t border-fn-border mt-1 pt-2' : ''}`}
            >
              {item.icon}
              {item.label}
            </button>
          ))}
        </div>
        </>
      )}

      <ConfirmModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete Offer"
        description={`Are you sure you want to delete "${deleteTarget?.name}"? This action cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
      />

      <OfferModal
        isOpen={isOfferModalOpen}
        editingOffer={editingOffer}
        onClose={() => { setIsOfferModalOpen(false); setEditingOffer(null) }}
        onSaved={handleOfferSaved}
      />
    </div>
  )
}
